const SAVE_VERSION = 1;

export type LevelProgress = Map<string, number>;
export type LevelStatus = "unresolved" | "solved" | "optimal";

export interface ProgressLevel {
  id: string;
  level: {
    level: number;
    name: string | null;
    minimumMoves: number | null;
  };
}

export interface LevelPresentation {
  status: LevelStatus;
  marker: "" | "🟡" | "🟢";
  className: "" | "level-solved" | "level-optimal";
  label: string;
}

interface SavedLevel {
  id: string;
  actions: number;
}

export interface SaveData {
  version: typeof SAVE_VERSION;
  levels: SavedLevel[];
}

export function createSaveData(
  progress: ReadonlyMap<string, number>,
): SaveData {
  return {
    version: SAVE_VERSION,
    levels: [...progress]
      .sort(([firstId], [secondId]) => firstId.localeCompare(secondId))
      .map(([id, actions]) => ({ id, actions })),
  };
}

function isValidLevelId(value: unknown): value is string {
  return (
    typeof value === "string" && value.length > 0 && value.trim() === value
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

export function parseSaveData(value: unknown): LevelProgress {
  if (!isRecord(value)) {
    throw new Error("The save file must contain an object");
  }

  if (value.version !== SAVE_VERSION || !Array.isArray(value.levels)) {
    throw new Error("Unsupported save file version");
  }

  const progress: LevelProgress = new Map();
  for (const level of value.levels) {
    if (!isRecord(level)) {
      throw new Error("Every level progress must be an object");
    }

    const id = level.id;
    const actions = level.actions;
    if (!isValidLevelId(id) || !isNonNegativeInteger(actions)) {
      throw new Error("Every level progress must contain a valid score");
    }
    progress.set(id, actions);
  }
  return progress;
}

export function stringifySaveData(
  progress: ReadonlyMap<string, number>,
): string {
  return JSON.stringify(createSaveData(progress), null, 2);
}

export function getLevelStatus(
  level: ProgressLevel,
  progress: ReadonlyMap<string, number>,
): LevelStatus {
  const actions = progress.get(level.id);
  if (actions === undefined) return "unresolved";
  if (
    level.level.minimumMoves !== null &&
    actions === level.level.minimumMoves
  ) {
    return "optimal";
  }
  return "solved";
}

export function getLevelPresentation(
  level: ProgressLevel,
  progress: ReadonlyMap<string, number>,
): LevelPresentation {
  const status = getLevelStatus(level, progress);
  const marker = status === "optimal" ? "🟢" : status === "solved" ? "🟡" : "";
  const className =
    status === "optimal"
      ? "level-optimal"
      : status === "solved"
        ? "level-solved"
        : "";
  const actions = progress.get(level.id);
  const score =
    actions === undefined
      ? ""
      : ` — ${actions} action${actions === 1 ? "" : "s"}`;
  const markerPrefix = marker === "" ? "" : `${marker} `;
  const name = level.level.name ?? "Niveau sans nom";

  return {
    status,
    marker,
    className,
    label: `${markerPrefix}${level.level.level}. ${name}${score}`,
  };
}

export function recordBestScore(
  progress: LevelProgress,
  levelId: string,
  actions: number,
): boolean {
  const previousBest = progress.get(levelId);
  if (previousBest !== undefined && previousBest <= actions) return false;

  progress.set(levelId, actions);
  return true;
}

export function findPreferredLevel<T extends ProgressLevel>(
  levels: readonly T[],
  progress: ReadonlyMap<string, number>,
): T | undefined {
  return (
    levels.find((level) => getLevelStatus(level, progress) === "unresolved") ??
    levels.find((level) => getLevelStatus(level, progress) !== "optimal") ??
    levels[0]
  );
}
